import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography, Card, Button, List, Space, Tag, Alert, Spin, Empty, Tooltip,
} from 'antd';
import { ReloadOutlined, MailOutlined } from '@ant-design/icons';
import { API_BASE_URL, getAuthHeaders } from '../api';

const { Text, Paragraph } = Typography;

interface ContactSubmissionRow {
  id: number;
  inquiry_type: 'athlete' | 'federation';
  name: string;
  email: string;
  organization: string | null;
  message: string;
  submitter_ip: string | null;
  created_at: string;
}

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
};

/**
 * Admin list of contact-us submissions from the landing page (admin-only API).
 */
const ContactSubmissionsAdminTab: React.FC = () => {
  const isMobile = useIsMobile();
  const [rows, setRows] = useState<ContactSubmissionRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setApiError(null);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE_URL}/api/admin/contact-submissions`, { headers });
      const text = await res.text();
      if (!res.ok) {
        setApiError(`${res.status}: ${text}`);
        return;
      }
      const data = JSON.parse(text) as { submissions?: ContactSubmissionRow[] };
      setRows(data.submissions || []);
    } catch (e: unknown) {
      setApiError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Card
      title={(
        <Space>
          <MailOutlined />
          Contact submissions
        </Space>
      )}
      styles={{ body: { padding: isMobile ? 12 : 24, overflowX: 'auto' } }}
      extra={(
        <Tooltip title="Refresh list">
          <Button icon={<ReloadOutlined />} size="small" onClick={load} loading={loading} />
        </Tooltip>
      )}
    >
      {apiError && (
        <Alert
          type="error"
          showIcon
          message="Error"
          closable
          description={<Text code style={{ fontSize: 11, wordBreak: 'break-all' }}>{apiError}</Text>}
          style={{ marginBottom: 16 }}
          onClose={() => setApiError(null)}
        />
      )}
      <Alert
        type="info"
        showIcon
        message="Messages from the public Contact us form on the landing page (latest 500)."
        style={{ marginBottom: 16 }}
      />
      <Spin spinning={loading}>
        {rows.length === 0 && !loading && !apiError ? (
          <Empty description="No contact submissions yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <List
            dataSource={rows}
            renderItem={(rec) => (
              <List.Item style={{ alignItems: 'flex-start' }}>
                <List.Item.Meta
                  avatar={<MailOutlined style={{ fontSize: 20, color: 'var(--blue-bell)', marginTop: 2 }} />}
                  title={(
                    <Space wrap>
                      <Text strong style={{ fontSize: isMobile ? 13 : 14 }}>{rec.name}</Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>{rec.email}</Text>
                      <Tag color={rec.inquiry_type === 'athlete' ? 'blue' : 'purple'}>
                        {rec.inquiry_type === 'athlete' ? 'Athlete' : 'Federation / community'}
                      </Tag>
                    </Space>
                  )}
                  description={(
                    <div style={{ marginTop: 8 }}>
                      {rec.organization ? (
                        <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                          Org: {rec.organization}
                        </Text>
                      ) : null}
                      <Paragraph
                        style={{ marginBottom: 8, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                      >
                        {rec.message}
                      </Paragraph>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {new Date(rec.created_at).toLocaleString()}
                        {rec.submitter_ip ? ` · IP ${rec.submitter_ip}` : ''}
                      </Text>
                    </div>
                  )}
                />
              </List.Item>
            )}
          />
        )}
      </Spin>
    </Card>
  );
};

export default ContactSubmissionsAdminTab;
